import { useCallback, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { formatPrice } from '../../utils/utils';
import { getLighterColor } from '../../utils/colors';
import moment from 'moment/moment';
import { defaultRankingAndActivity, leadActivityCategories } from '../../constants/sales';

const LeadCard = (props) => {
  const {
    id,
    lead,
    surgeons = [],
    labels = [],
    onClick,
    tenantColor,
    isOverlay,
    rottingPeriod,
    surgeonRanking,
  } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging && !isOverlay ? 0 : 1,
    zIndex: isDragging && !isOverlay ? 0 : 1,
  };

  const getSurgeonName = useCallback((surgeonId) => {
    const surgeon = surgeons.find((s) => s.id === surgeonId);
    return surgeon ? `${surgeon.title} ${surgeon.firstName} ${surgeon.lastName}` : '';
  }, [surgeons]);

  const getLabelName = useCallback((labelId) => {
    const label = labels.find((l) => l.id === labelId);
    return label?.name || '';
  }, [labels]);

  const getLabelColor = useCallback((labelId) => {
    const label = labels.find((l) => l.id === labelId);
    return label?.color || '#000000';
  }, [labels]);

  const leadLabels = useMemo(() => lead?.labels?.filter((labelId) => labels.find((l) => l.id === labelId)), [lead, labels]);

  const rottingDays = useMemo(() => lead?.stageUpdatedAt && moment().startOf('day').diff(moment(lead?.stageUpdatedAt), 'days'), [lead]);

  const rankingScore = useMemo(() => {
    if (!lead) {
      return '';
    }

    const { ranking } = lead;

    if (!ranking) {
      return '';
    }

    const { teaching, kolStatus, published, training } = ranking;
    const sum = teaching + kolStatus + published + training;
    const _ranking = surgeonRanking || defaultRankingAndActivity;

    if (sum && sum <= 20 && _ranking) {
      return _ranking[sum].rankCategory || 'E';
    }

    return '';
  }, [lead, surgeonRanking]);

  const lastMonthActivity = useMemo(() => {
    if (!lead || !lead.activity?.length) {
      return 0;
    }

    const dateStart = moment().subtract(1, 'month').startOf('month');
    const dateEnd = moment().subtract(1, 'month').endOf('month');

    const bookingActivity = lead?.activity?.filter((a) => [leadActivityCategories.TRAINING.value, leadActivityCategories.CALL.value, leadActivityCategories.MEETING.value, leadActivityCategories.DEMO.value].includes(a?.category));
    const stageActivity = lead?.activity?.filter((a) => a.type === 'LEAD_STAGE_UPDATED' && dateStart.isBefore(a.createdAt) && dateEnd.isAfter(a.createdAt))
    const lastMonthActivity = bookingActivity?.filter((a) => dateStart.isBefore(a.bookingData?.date) && dateEnd.isAfter(a.bookingData?.date));
    const activityNumber = stageActivity?.length + lastMonthActivity?.length;

    return activityNumber || 0;
  }, [lead]);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ ...style, '--colorHover': tenantColor }}
      className={`lead-card${rottingDays > rottingPeriod ? ' lead-card__rotten' : ''}`}
      onClick={onClick}
    >
      {!!leadLabels?.length && (
        <div className="d-flex space-between">
          <div className="d-flex m-b-md">
            {leadLabels.map((labelId) => (
              <div
                key={labelId}
                className="lead-card__label"
                style={{
                  '--labelColor': getLabelColor(labelId),
                  '--backgroundColor': getLighterColor(getLabelColor(labelId)),
                }}
              >
                {getLabelName(labelId)}
              </div>
            ))}
          </div>
          {rottingDays > rottingPeriod && (
            <div className="d-flex font-size-sm align-end" style={{ color: '#F53434' }}>
              <div className="font-bold">{rottingDays}</div>
              <div className="m-l-sm">{`day${rottingDays > 1 ? 's' : ''}`}</div>
            </div>
          )}
        </div>
      )}
      <div className="font-bold">{getSurgeonName(lead.surgeon)}</div>
      <div className="m-t-sm secondary">{lead.title}</div>

      <div className="d-flex space-between" style={{ marginTop: 12 }}>
        <div className="flex-1">
          <div>Value</div>
          <div className='font-bold' style={{ marginTop: 2 }}>{lead.anticipatedValue ? `$${formatPrice(lead.anticipatedValue, 0)}` : 'N/A'}</div>
        </div>
        <div className="flex-1">
          <div className="text-center">Activity</div>
          <div className="font-bold text-center" style={{ marginTop: 2 }}>{lastMonthActivity}</div>
        </div>
        <div className="flex-1">
          <div className="text-right">Rank</div>
          <div className="font-bold text-right" style={{ marginTop: 2, marginRight: 12 }}>{rankingScore || '–'}</div>
        </div>
      </div>
      {/*<div className='d-flex m-t-sm space-between'>*/}
      {/*  {!!lead.anticipatedValue ? (*/}
      {/*    <div className='secondary'>{`$${formatPrice(lead.anticipatedValue, 0)}`}</div>*/}
      {/*  ) : <div/>}*/}
      {/*  {rottingDays > rottingPeriod && (*/}
      {/*    <div className='d-flex font-size-sm align-end' style={{ color: '#F53434' }}>*/}
      {/*      <div className='font-bold'>{rottingDays}</div>*/}
      {/*      <div className='m-l-sm'>{`day${rottingDays > 1 ? 's' : ''}`}</div>*/}
      {/*    </div>*/}
      {/*  )}*/}
      {/*</div>*/}
    </div>
  );
};

export default LeadCard;
